/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module ItemsModule */

import { GSLBVSCollection } from 'ng/modules/collections/factories/gslb-vs.collection.factory';
import { IItemArgs } from 'ajs/modules/data-model/factories/item.factory';
import { VirtualServiceItem } from './virtual-service.item.factory';

import * as l10n from './gslb-vs.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

interface IGslbVsItemArgs extends IItemArgs<any> {
    hostAllSubdomainsDnsVsUuids?: string[];
    siteDnsVsUuids?: string[];
}

/**
 * @description Wrapper around VirtualService item class with GSLB DNS VS-specific properties.
 * @author alextsg
 */
export class GSLBVSItem extends VirtualServiceItem {
    protected collection: GSLBVSCollection;

    /**
     * Used to check if a GSLB VS currently hosts all subdomains in the GSLB site.
     */
    private readonly hostAllSubdomainsDnsVsUuidsSet: Set<string>;

    /**
     * Used to check if a GSLB VS is configured in a GSLB site.
     */
    private readonly siteDnsVsUuidsSet: Set<string>;

    constructor(args: IGslbVsItemArgs = {}) {
        super(args);

        const {
            hostAllSubdomainsDnsVsUuids,
            siteDnsVsUuids,
        } = args;

        const l10nService = this.getNgDependency('l10nService');

        l10nService.registerSourceBundles(dictionary);

        if (hostAllSubdomainsDnsVsUuids) {
            this.hostAllSubdomainsDnsVsUuidsSet = new Set(hostAllSubdomainsDnsVsUuids);
        }

        if (siteDnsVsUuids) {
            this.siteDnsVsUuidsSet = new Set(siteDnsVsUuids);
        }
    }

    /**
     * @override
     * Return the name of the GSLB DNS VS, suffixed by a label related to subdomains if available.
     */
    public getName(): string {
        const name = super.getName();
        const l10nService = this.getNgDependency('l10nService');

        if (this.siteDnsVsUuidsSet && !this.siteDnsVsUuidsSet.has(this.id)) {
            return `${name} (${l10nService.getMessage(l10nKeys.noSubdomainsLabel)})`;
        }

        if (this.hostAllSubdomainsDnsVsUuidsSet?.has(this.id)) {
            return `${name} (${l10nService.getMessage(l10nKeys.hostAllSubdomainsLabel)})`;
        }

        return name;
    }
}
